import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { Reveal } from "../components/common/Section";
import { Button } from "../components/ui/button";
import { INSIGHTS } from "../data/content";
import { useContent } from "../cms/ContentContext";

export default function InsightDetail() {
  const { slug } = useParams();
  const { content } = useContent();
  const source = content.insights?.length ? content.insights : INSIGHTS;
  const article = source.find((a) => a.slug === slug);

  useEffect(() => {
    document.title = article ? `${article.title} | norse3` : "Insights | norse3";
  }, [article]);

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-5 lg:px-8 pt-36 pb-24 text-center">
        <h1 className="text-3xl font-bold text-foreground">Article not found</h1>
        <Button asChild className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90">
          <Link to="/insights">Back to Insights</Link>
        </Button>
      </div>
    );
  }

  const other = source.find((a) => a.slug !== slug);

  return (
    <div>
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0 grid-backdrop opacity-40" aria-hidden="true" />
        <div className="absolute inset-0 radial-fade" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-5 lg:px-8 pt-32 pb-12 lg:pt-36">
          <Link to="/insights" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> All insights
          </Link>
          <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-primary">{article.category}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {article.readTime}</span>
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-foreground leading-tight text-balance">
            {article.title}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">{article.excerpt}</p>
        </div>
      </section>

      <article className="py-14 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 space-y-5">
          {article.body.map((p, i) => (
            <Reveal key={i} delay={i * 40}>
              <p className="text-[1.05rem] leading-relaxed text-foreground/90">{p}</p>
            </Reveal>
          ))}

          <div className="mt-10 rounded-lg border border-primary/25 bg-primary/[0.06] p-7">
            <p className="text-lg font-semibold text-foreground">See where norse3 sits in your estate.</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Book a walkthrough and see how the control and evidence path applies to your
              conversations.
            </p>
            <Button asChild className="mt-5 bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </article>

      {other && (
        <section className="border-t border-white/10 py-12">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Read next</p>
            <Link to={`/insights/${other.slug}`} className="group mt-3 block rounded-xl border border-white/10 bg-navy-900/60 p-6 hover:border-primary/30 transition-colors">
              <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">{other.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{other.excerpt}</p>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
